import { useEffect, useState, useContext, useCallback } from "react";
import { ManageIcon } from "./AIBotTab_1";
import { userSlice } from "@/redux/User";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  AscendingOrderSvg,
  CharactersIconSvg,
  DescendingOrderSVG,
  LinkRejectedIcon,
  ReSyncingFailedIcon,
  ReSyncingIcon,
  TrainingCompletedIcon,
  TrainingProgressIcon,
} from "./AiBotAssets";
import moment from "moment";
import Pagination from "../Pagination";
import { ConfigId } from "@/constants/ConfigData";
import {
  ConfluenceIcon,
  GoogleDriveIcon,
  NotionIcon,
  SalesforceIcon,
} from "./AiBotAssets";
import { BotContext } from "../store";
import TextAreaComponent from "./TextAreaComponent";

interface dropdowns {
  linkStatus: boolean;
  fileStatus: boolean;
  fileAction: boolean;
  syncTime: boolean;
  linkCharacters: boolean;
  linkLastSynced: boolean;
  linkAutoSync: boolean;
}

const dropdownState = {
  linkStatus: false,
  fileStatus: false,
  fileAction: false,
  syncTime: false,
  linkCharacters: false,
  linkLastSynced: false,
  linkAutoSync: false,
};

const AllTrainedFiles = ({
  botData,
  trainedFiles,
  setSelectedSourceTab,
  selectedSourceTab,
  characterUsed,
  characterLimit,
  isOld,
}) => {
  const [store, storeDispatch] = useContext(BotContext);
  const dataSources = store.botData.dataSources;
  const trainedSource = store.botData.aiSource;
  const urls = botData.aiSource?.urls || [];
  const youtube = botData.aiSource?.youtube || [];
  const combinedUrls = [...youtube, ...urls];
  const defaultFilteredUrls = combinedUrls.filter(
    (link) => link.status === "Training Complete",
  );
  const defaultFilteredFiles = (trainedFiles || []).filter(
    (file) => file.status === "Training Complete",
  );
  const [inProcess, setInProcess] = useState(false);
  const [searchedLinks, setSearchedLinks] = useState(defaultFilteredUrls);
  const [paginatedLinks, setPaginatedLinks] = useState(defaultFilteredUrls);
  const [searchedFiles, setSearchedFiles] = useState(defaultFilteredFiles);
  const [paginatedFiles, setPaginatedFiles] = useState(defaultFilteredFiles);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [fileSearchQuery2, setFileSearchQuery2] = useState("");
  const [drpToggle, setDrpToggle] = useState<dropdowns>(dropdownState);
  const [nativeAppSearch, setNativeAppSearch] = useState<{
    [key: string]: string;
  }>({});
  const [nativeAppPage, setNativeAppPage] = useState<{ [key: string]: number }>(
    {},
  );
  const dispatch = useDispatch();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const itemsPerPage = 10;
  const [fileCurrentPage, setFileCurrentPage] = useState(1);
  const renderFileType = useCallback((type) => {
    return type.split(".").at(-1);
  }, []);

  function toggleDropdowns(type: string, show: boolean = false) {
    if (show) {
      setDrpToggle({ ...dropdownState, [type]: show });
    } else {
      setDrpToggle(dropdownState);
    }
  }
  const sortByCharactersAscending = (setSearchedLinks, setPaginatedLinks) => {
    const sortedLinks = [...searchedLinks].sort(
      (a, b) => a.characterCount - b.characterCount,
    );
    setSearchedLinks(sortedLinks);
    setCurrentPage(1);
    setPaginatedLinks(sortedLinks.slice(0, itemsPerPage));
    dispatch(
      userSlice.actions.setToastPopUp({
        show: true,
        toastType: "success",
        message: "Links sorted in ascending order",
      }),
    );
  };

  const handleSearchFiles2 = (event) => {
    const selectedFileStatus2 = { text: "Training Complete" };
    const value = event.target.value;
    setFileSearchQuery2(value);
    setFileCurrentPage(1);
    filterFiles(
      value,
      setSearchedFiles,
      setPaginatedFiles,
      selectedFileStatus2,
    );
  };

  const filterFiles = (
    searchQuery,
    setSearchedFiles,
    setPaginatedFiles,
    selectedFileStatus,
  ) => {
    const statusFilteredFiles =
      selectedFileStatus.text === "All Statuses" ||
      selectedFileStatus.text === ""
        ? trainedFiles
        : trainedFiles.filter(
            (file) => file.status === selectedFileStatus.text,
          );
    if (!searchQuery) {
      setSearchedFiles(statusFilteredFiles);
      setPaginatedFiles(statusFilteredFiles.slice(0, itemsPerPage));
      return;
    }
    const filtered = statusFilteredFiles.filter((file) =>
      (file.title || file.name)
        .toLowerCase()
        .includes(searchQuery.toLowerCase()),
    );

    setSearchedFiles(filtered);
    setPaginatedFiles(filtered.slice(0, itemsPerPage));
  };

  const sortByCharactersDescending = (setSearchedLinks, setPaginatedLinks) => {
    const sortedLinks = [...searchedLinks].sort(
      (a, b) => b.characterCount - a.characterCount,
    );
    setSearchedLinks(sortedLinks);
    setCurrentPage(1);
    setPaginatedLinks(sortedLinks.slice(0, itemsPerPage));
    dispatch(
      userSlice.actions.setToastPopUp({
        show: true,
        toastType: "success",
        message: "Links sorted in descending order",
      }),
    );
  };
  const sortByLastSyncedDescending = (setSearchedLinks, setPaginatedLinks) => {
    // Separate "In Progress" items
    const inProgress = searchedLinks.filter(
      (link) => link.status === "In Progress",
    );
    const others = searchedLinks.filter(
      (link) => link.status !== "In Progress",
    );

    // Sort the rest by lastSyncTime (descending)
    const sortedOthers = [...others].sort(
      (a, b) => Number(b.lastSyncTime) - Number(a.lastSyncTime),
    );

    // Concatenate sorted + inProgress
    const sortedLinks = [...sortedOthers, ...inProgress];

    setSearchedLinks(sortedLinks);
    setCurrentPage(1);
    setPaginatedLinks(sortedLinks.slice(0, itemsPerPage));

    dispatch(
      userSlice.actions.setToastPopUp({
        show: true,
        toastType: "success",
        message: "Links sorted by last synced time in descending order",
      }),
    );
  };

  const sortByLastSyncedAscending = (setSearchedLinks, setPaginatedLinks) => {
    // Separate "In Progress" items
    const inProgress = searchedLinks.filter(
      (link) => link.status === "In Progress",
    );
    const others = searchedLinks.filter(
      (link) => link.status !== "In Progress",
    );

    // Sort the rest by lastSyncTime (ascending)
    const sortedOthers = [...others].sort(
      (a, b) => Number(a.lastSyncTime) - Number(b.lastSyncTime),
    );

    // Concatenate sorted + inProgress
    const sortedLinks = [...sortedOthers, ...inProgress];

    setSearchedLinks(sortedLinks);
    setCurrentPage(1);
    setPaginatedLinks(sortedLinks.slice(0, itemsPerPage));

    dispatch(
      userSlice.actions.setToastPopUp({
        show: true,
        toastType: "success",
        message: "Links sorted by last synced time in ascending order",
      }),
    );
  };

  const handleSearch2 = (event) => {
    const selectedStatus2 = { text: "Training Complete" };
    const value = event.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    filterLinks(value, setSearchedLinks, setPaginatedLinks, selectedStatus2);
  };

  const filterLinks = (
    searchQuery,
    setSearchedLinks,
    setPaginatedLinks,
    selectedStatus,
  ) => {
    const statusFilteredLinks =
      selectedStatus.text === "All Statuses" || selectedStatus.text === ""
        ? combinedUrls
        : combinedUrls.filter((link) => link.status === selectedStatus.text);

    if (!searchQuery) {
      setSearchedLinks(statusFilteredLinks);
      setPaginatedLinks(statusFilteredLinks.slice(0, itemsPerPage));
      return;
    }

    const filtered = statusFilteredLinks.filter((link) =>
      link.url.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    setSearchedLinks(filtered);
    setPaginatedLinks(filtered.slice(0, itemsPerPage));
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  const handleFilePageChange = (newPage) => {
    setFileCurrentPage(newPage);
  };
  useEffect(() => {
    if (combinedUrls) {
      setSearchedLinks(defaultFilteredUrls);
    }
    if (trainedFiles) {
      setSearchedFiles(defaultFilteredFiles);
    }
  }, [botData]);

  useEffect(() => {
    paginateLinks();
  }, [currentPage, searchedLinks]);
  useEffect(() => {
    paginateFiles();
  }, [fileCurrentPage, searchedFiles]);

  const paginateLinks = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedLinks(searchedLinks.slice(startIndex, endIndex));
  };

  const paginateFiles = () => {
    const startIndex = (fileCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedFiles(searchedFiles.slice(startIndex, endIndex));
  };

  return Object.keys(botData.aiSource).some((key) => {
    if (key !== "textArea" && !!botData.aiSource[key]?.length) {
      return true;
    } else if (key === "textArea" && botData.aiSource[key]?.contentId) {
      return true;
    }
    return false;
  }) ? (
    <div className="flex h-full flex-col gap-[20px] overflow-auto">
      {combinedUrls.length ? (
        <div className="flex h-max flex-col">
          <h2 className="text-[14px] font-medium text-text-clr">
            Trained Links
          </h2>
          <div className="trained_links_wrapper p-relative new-scrollbar mt-10">
            {combinedUrls?.length ? (
              <>
                <div className="table_actions sticky left-0 top-0 z-[1] flex items-center justify-between gap-10 bg-[#f9f9f9] px-[20px] py-3">
                  <div className="flex items-center gap-10">
                    <div className="search_div border-[rgba(0, 0, 0, 0.10)] flex w-[300px] items-center gap-10 rounded-[10px] border border-solid bg-white px-[16px] py-[13px]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={15}
                        height={14}
                        viewBox="0 0 15 14"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_190_16528)">
                          <path
                            d="M5.60625 0C6.98538 0 8.30802 0.547855 9.28321 1.52304C10.2584 2.49823 10.8063 3.82087 10.8063 5.2C10.8063 6.488 10.3342 7.672 9.55825 8.584L9.77425 8.8H10.4062L14.4062 12.8L13.2063 14L9.20625 10V9.368L8.99025 9.152C8.04669 9.95742 6.84682 10.3999 5.60625 10.4C4.22712 10.4 2.90448 9.85215 1.92929 8.87696C0.954105 7.90177 0.40625 6.57913 0.40625 5.2C0.40625 3.82087 0.954105 2.49823 1.92929 1.52304C2.90448 0.547855 4.22712 0 5.60625 0ZM5.60625 1.6C3.60625 1.6 2.00625 3.2 2.00625 5.2C2.00625 7.2 3.60625 8.8 5.60625 8.8C7.60625 8.8 9.20625 7.2 9.20625 5.2C9.20625 3.2 7.60625 1.6 5.60625 1.6Z"
                            fill="#444444"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_190_16528">
                            <rect
                              width={14}
                              height={14}
                              fill="white"
                              transform="translate(0.40625)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                      <input
                        type="text"
                        placeholder="Search link"
                        className="h-[unset] border-none bg-transparent p-0 outline-none"
                        value={searchTerm}
                        onChange={handleSearch2}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-10">
                    <button
                      className={`manage-links-btn flex items-center gap-[5px] whitespace-nowrap ${inProcess ? "pointer-events-none opacity-50" : ""}`}
                      onClick={() => {
                        setSelectedSourceTab("source_link");
                      }}
                    >
                      <ManageIcon /> Manage Links
                    </button>
                  </div>
                </div>
                <table
                  className={`links_table w-100 all_trained_links_table mt-0 ${inProcess && "pointer-events-none opacity-50"}`}
                >
                  <thead className="w-100 flex">
                    <tr>
                      <th className="flex items-center">
                        <span className="pl-[16px] font-medium">Links</span>
                      </th>
                      <th>Status</th>
                      <th
                        className="order_drp relative flex cursor-pointer items-center gap-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdowns(
                            "linkCharacters",
                            !drpToggle.linkCharacters,
                          );
                        }}
                        onMouseLeave={() =>
                          toggleDropdowns("linkCharacters", false)
                        }
                      >
                        <span className="flex items-center gap-[8px] font-medium">
                          Characters <CharactersIconSvg />
                        </span>

                        {drpToggle.linkCharacters && (
                          <ul className="status_dropdown_list absolute left-0 top-[50px] z-[1] overflow-hidden rounded-[8px] bg-white shadow-md">
                            <h4 className="text-left !text-[12px] uppercase !text-[rgba(68,68,68,0.70)]">
                              Order
                            </h4>
                            {order.map((item, index) => (
                              <li
                                key={index}
                                className="flex cursor-pointer items-center gap-[5px] px-4 py-2 hover:bg-gray-200"
                                onClick={() => {
                                  toggleDropdowns("linkCharacters", false);
                                  if (item.text === "Ascending") {
                                    sortByCharactersAscending(
                                      setSearchedLinks,
                                      setPaginatedLinks,
                                    );
                                  } else {
                                    sortByCharactersDescending(
                                      setSearchedLinks,
                                      setPaginatedLinks,
                                    );
                                  }
                                }}
                              >
                                {item.iconComponent} {item.text}
                              </li>
                            ))}
                          </ul>
                        )}
                      </th>
                      <th
                        className="order_drp relative flex cursor-pointer items-center gap-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdowns(
                            "linkLastSynced",
                            !drpToggle.linkLastSynced,
                          );
                        }}
                        onMouseLeave={() =>
                          toggleDropdowns("linkLastSynced", false)
                        }
                      >
                        <span className="flex items-center gap-[8px] font-medium">
                          Last-Synced <CharactersIconSvg />
                        </span>

                        {drpToggle.linkLastSynced && (
                          <ul className="status_dropdown_list absolute left-0 top-[50px] z-[1] overflow-hidden rounded-[8px] bg-white shadow-md">
                            <h4 className="text-left !text-[12px] uppercase !text-[rgba(68,68,68,0.70)]">
                              Order
                            </h4>
                            {order.map((item, index) => (
                              <li
                                key={index}
                                className="flex cursor-pointer items-center gap-[5px] px-4 py-2 hover:bg-gray-200"
                                onClick={() => {
                                  toggleDropdowns("linkLastSynced", false);
                                  if (item.text == "Ascending") {
                                    sortByLastSyncedAscending(
                                      setSearchedLinks,
                                      setPaginatedLinks,
                                    );
                                  } else {
                                    sortByLastSyncedDescending(
                                      setSearchedLinks,
                                      setPaginatedLinks,
                                    );
                                  }
                                }}
                              >
                                {item.iconComponent} {item.text}
                              </li>
                            ))}
                          </ul>
                        )}
                      </th>
                    </tr>
                  </thead>

                  <tbody className="flex !h-max flex-col items-center justify-start overflow-auto">
                    {paginatedLinks.length > 0 ? (
                      paginatedLinks.map((data, index) => {
                        const actualIndex =
                          (currentPage - 1) * itemsPerPage + index;
                        return (
                          <tr
                            className={`w-100 flex flex-col ${data?.status === "In Progress" || inProcess ? "pointer-events-none opacity-50" : ""}`}
                            key={actualIndex}
                          >
                            <td className="flex items-center">
                              <span
                                className="ml-[16px] overflow-hidden whitespace-nowrap"
                                onMouseEnter={(e) => {
                                  const container = e.currentTarget;
                                  const inner = container.querySelector(
                                    ".scrolling-text",
                                  ) as HTMLElement | null;
                                  if (inner) {
                                    const scrollDistance =
                                      inner.scrollWidth - container.offsetWidth;
                                    if (scrollDistance > 0) {
                                      const speed = 100; // pixels per second
                                      const duration = scrollDistance / speed;
                                      inner.style.transition = `transform ${duration}s linear`;
                                      inner.style.transform = `translateX(-${scrollDistance}px)`;
                                    }
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.currentTarget;
                                  const inner = container.querySelector(
                                    ".scrolling-text",
                                  ) as HTMLElement | null;
                                  if (inner) {
                                    inner.style.transition =
                                      "transform 0.3s ease-out";
                                    inner.style.transform = "translateX(0)";
                                  }
                                }}
                              >
                                <div
                                  className="scrolling-text whitespace-nowrap"
                                  title={data.url}
                                >
                                  {data.url}
                                </div>
                              </span>
                            </td>
                            <td
                              className={`flex items-center gap-[6px] whitespace-nowrap ${data?.status === "In Progress" && "rotating-svg"} ${data?.status === "Re-syncing" && "resyncing-svg"}`}
                            >
                              {
                                statuses.find(
                                  (item) => item.text === data.status,
                                )?.iconComponent
                              }
                              {data.status}
                            </td>

                            <td>
                              {data.characterCount ??
                                data.transcript?.length ??
                                0}
                            </td>
                            <td>
                              {isNaN(Number(data.lastSyncTime)) ||
                              !data.lastSyncTime
                                ? "..."
                                : moment(Number(data.lastSyncTime)).fromNow()}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <div className="no_links_fetched pd-30 h-100 grid place-items-center">
                        <img
                          src="https://dlvkyia8i4zmz.cloudfront.net/7eUKmJsvR0OTNIC4DKVQ_1.svg"
                          alt="no links fetched"
                        />
                      </div>
                    )}
                  </tbody>
                  <tfoot className="flex h-[60px] items-center justify-between bg-[#f9f9f9] px-[30px] py-[16px]">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(
                        searchedLinks.length / itemsPerPage,
                      )}
                      totalItems={searchedLinks.length}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handlePageChange}
                      showFirstLast={true}
                      showItemsInfo={true}
                    />
                  </tfoot>
                </table>
              </>
            ) : (
              <div className="no_links_fetched pd-30 h-100 grid place-items-center">
                <img
                  src="https://dlvkyia8i4zmz.cloudfront.net/7eUKmJsvR0OTNIC4DKVQ_1.svg"
                  alt="no links fetched"
                />
              </div>
            )}
          </div>
        </div>
      ) : null}

      {appCards.some(
        (item) =>
          (dataSources && dataSources[item.key]?.length > 0) ||
          (trainedSource && trainedSource[item.key]?.length > 0),
      ) && (
        <div className="fx-d-col source_native_apps flex">
          <div className="flex items-center justify-between">
            <h3>Native Apps</h3>
            <span
              className="mb-10 flex cursor-pointer items-center gap-[5px] text-[14px] text-[#fb5f66]"
              onClick={() => setSelectedSourceTab("native_apps")}
            >
              <svg
                width={21}
                height={20}
                viewBox="0 0 21 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_915_36725)">
                  <path
                    d="M10.4063 0C8.42844 0 6.49504 0.58649 4.85055 1.6853C3.20606 2.78412 1.92434 4.3459 1.16746 6.17317C0.410583 8.00043 0.212549 10.0111 0.598402 11.9509C0.984254 13.8907 1.93666 15.6725 3.33519 17.0711C4.73371 18.4696 6.51554 19.422 8.45535 19.8079C10.3952 20.1937 12.4058 19.9957 14.2331 19.2388C16.0603 18.4819 17.6221 17.2002 18.7209 15.5557C19.8198 13.9112 20.4063 11.9778 20.4063 10C20.4034 7.34872 19.3489 4.80684 17.4742 2.9321C15.5994 1.05736 13.0575 0.00286757 10.4063 0ZM10.4063 18.3333C8.75808 18.3333 7.14691 17.8446 5.7765 16.9289C4.40609 16.0132 3.33799 14.7117 2.70726 13.189C2.07653 11.6663 1.9115 9.99076 2.23304 8.37425C2.55459 6.75774 3.34826 5.27288 4.5137 4.10744C5.67914 2.94201 7.16399 2.14833 8.7805 1.82679C10.397 1.50525 12.0726 1.67027 13.5953 2.301C15.118 2.93173 16.4195 3.99984 17.3352 5.37025C18.2508 6.74066 18.7396 8.35182 18.7396 10C18.7372 12.2094 17.8584 14.3276 16.2961 15.8899C14.7339 17.4522 12.6156 18.3309 10.4063 18.3333ZM14.5729 10C14.5729 10.221 14.4851 10.433 14.3288 10.5893C14.1726 10.7455 13.9606 10.8333 13.7396 10.8333H11.2396V13.3333C11.2396 13.5543 11.1518 13.7663 10.9955 13.9226C10.8392 14.0789 10.6273 14.1667 10.4063 14.1667C10.1852 14.1667 9.97328 14.0789 9.817 13.9226C9.66072 13.7663 9.57292 13.5543 9.57292 13.3333V10.8333H7.07292C6.85191 10.8333 6.63995 10.7455 6.48367 10.5893C6.32738 10.433 6.23959 10.221 6.23959 10C6.23959 9.77899 6.32738 9.56703 6.48367 9.41074C6.63995 9.25447 6.85191 9.16667 7.07292 9.16667H9.57292V6.66667C9.57292 6.44565 9.66072 6.23369 9.817 6.07741C9.97328 5.92113 10.1852 5.83333 10.4063 5.83333C10.6273 5.83333 10.8392 5.92113 10.9955 6.07741C11.1518 6.23369 11.2396 6.44565 11.2396 6.66667V9.16667H13.7396C13.9606 9.16667 14.1726 9.25447 14.3288 9.41074C14.4851 9.56703 14.5729 9.77899 14.5729 10Z"
                    fill="#FB5F66"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_915_36725">
                    <rect
                      width={20}
                      height={20}
                      fill="white"
                      transform="translate(0.40625)"
                    />
                  </clipPath>
                </defs>
              </svg>
              Add Apps
            </span>
          </div>
          <div className="apps_wrap grid gap-10">
            {appCards.map((item, index) => {
              const hasImportedDocs =
                (dataSources && dataSources[item.key]?.length > 0) ||
                (trainedSource && trainedSource[item.key]?.length > 0);
              const searchValue = nativeAppSearch[item.key] || "";
              const pageValue = nativeAppPage[item.key] || 1;
              const allDocs = trainedSource[item.key] || [];
              const filteredDocs = searchValue
                ? allDocs.filter((doc) =>
                    (doc.title || doc.name || "")
                      .toLowerCase()
                      .includes(searchValue.toLowerCase()),
                  )
                : allDocs;
              const paginatedDocs = filteredDocs.slice(
                (pageValue - 1) * itemsPerPage,
                pageValue * itemsPerPage,
              );
              return hasImportedDocs ? (
                <div className="source_native_app_card" key={item.key}>
                  <div className="app grid gap-10">
                    <div className="w-100 flex items-center justify-between">
                      <div className="right_content flex items-center gap-[12px]">
                        {item.icon}
                        <div>
                          <h4>{item.title}</h4>
                          <p>{item.subtitle}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-[10px]"></div>
                    </div>
                    {trainedSource && !!trainedSource[item.key]?.length && (
                      <div className="pd-15 imported_docs">
                        <div
                          className="left-[0] right-[0] flex cursor-pointer items-center justify-between"
                          onClick={() =>
                            setExpandedIndex((prevIndex) =>
                              prevIndex === index ? null : index,
                            )
                          }
                        >
                          <h4 className="mb-0">Imported Documents</h4>
                          <div
                            className="flex items-center gap-[8px]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {expandedIndex === index && (
                              <div className="search_div border-[rgba(0, 0, 0, 0.10)] mr-2 flex w-[220px] items-center gap-10 rounded-[10px] border border-solid bg-white px-[12px] py-[8px]">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width={15}
                                  height={14}
                                  viewBox="0 0 15 14"
                                  fill="none"
                                >
                                  <g clipPath="url(#clip0_190_16528)">
                                    <path
                                      d="M5.60625 0C6.98538 0 8.30802 0.547855 9.28321 1.52304C10.2584 2.49823 10.8063 3.82087 10.8063 5.2C10.8063 6.488 10.3342 7.672 9.55825 8.584L9.77425 8.8H10.4062L14.4062 12.8L13.2063 14L9.20625 10V9.368L8.99025 9.152C8.04669 9.95742 6.84682 10.3999 5.60625 10.4C4.22712 10.4 2.90448 9.85215 1.92929 8.87696C0.954105 7.90177 0.40625 6.57913 0.40625 5.2C0.40625 3.82087 0.954105 2.49823 1.92929 1.52304C2.90448 0.547855 4.22712 0 5.60625 0ZM5.60625 1.6C3.60625 1.6 2.00625 3.2 2.00625 5.2C2.00625 7.2 3.60625 8.8 5.60625 8.8C7.60625 8.8 9.20625 7.2 9.20625 5.2C9.20625 3.2 7.60625 1.6 5.60625 1.6Z"
                                      fill="#444444"
                                    />
                                  </g>
                                  <defs>
                                    <clipPath id="clip0_190_16528">
                                      <rect
                                        width={15}
                                        height={14}
                                        fill="white"
                                        transform="translate(0.40625)"
                                      />
                                    </clipPath>
                                  </defs>
                                </svg>
                                <input
                                  type="text"
                                  placeholder={`Search ${item.title}`}
                                  className="h-[unset] border-none bg-transparent text-[14px] outline-none"
                                  value={nativeAppSearch[item.key] || ""}
                                  onChange={(e) => {
                                    setNativeAppSearch((prev) => ({
                                      ...prev,
                                      [item.key]: e.target.value,
                                    }));
                                    setNativeAppPage((prev) => ({
                                      ...prev,
                                      [item.key]: 1,
                                    }));
                                  }}
                                />
                              </div>
                            )}
                            <svg
                              className={`cp ${expandedIndex === index ? "" : "rotate-180"}`}
                              xmlns="http://www.w3.org/2000/svg"
                              width={11}
                              height={11}
                              viewBox="0 0 11 11"
                              fill="none"
                            >
                              <g clipPath="url(#clip0_2220_130208)">
                                <path
                                  d="M0.483887 7.73438L5.69222 2.31771L10.9006 7.73437L0.483887 7.73438Z"
                                  fill="#444444"
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_2220_130208">
                                  <rect
                                    width={9.61538}
                                    height={10}
                                    fill="white"
                                    transform="matrix(1 0 0 -1 0.884766 10.2344)"
                                  />
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                        </div>
                        <AnimatePresence>
                          {expandedIndex === index && (
                            <motion.div
                              initial={{
                                opacity: 0,
                                height: 0,
                                overflow: "hidden",
                              }}
                              animate={{
                                opacity: 1,
                                height: "auto",
                                overflow: "scroll",
                              }}
                              exit={{
                                opacity: 0,
                                height: 0,
                                overflow: "hidden",
                              }}
                              transition={{
                                duration: 0.3,
                                ease: "easeInOut",
                              }}
                            >
                              <table
                                className={`links_table w-100 mb-[15px] mt-0 ${inProcess && "pointer-events-none opacity-50"}`}
                              >
                                <thead
                                  className={`w-100 flex rounded-t-[8px] border border-[rgba(0,0,0,0.10)] bg-[#f2f2f2]`}
                                >
                                  <tr>
                                    <th className="flex items-center">
                                      <span className="pl-[16px] font-medium">
                                        Document Name
                                      </span>
                                    </th>
                                    <th>Status</th>
                                    <th className="order_drp relative flex cursor-pointer items-center gap-6">
                                      <span className="flex items-center gap-[8px]">
                                        Characters
                                      </span>
                                    </th>
                                    <th className="order_drp relative flex cursor-pointer items-center gap-6">
                                      <span className="flex items-center gap-[8px]">
                                        Last Updated
                                      </span>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="grey-scrollbar link-dynamic-height flex !h-max flex-col items-center justify-start overflow-auto border border-t-0 border-[rgba(0,0,0,0.10)] bg-white">
                                  {paginatedDocs.length > 0 ? (
                                    paginatedDocs.map(
                                      (innerItem, itemIndex) => (
                                        <tr
                                          // className={`w-100 flex flex-col ${innerItem.status === "In Progress" || inProcess ? "pointer-events-none opacity-50" : ""}`}

                                          className={`w-100 flex flex-col ${inProcess ? "pointer-events-none opacity-50" : ""}`}
                                          key={`trained-${innerItem?.googlePickerId || innerItem.id || itemIndex}`}
                                        >
                                          <td className="flex items-center">
                                            <span
                                              className="ml-[16px] overflow-hidden whitespace-nowrap"
                                              onMouseEnter={(e) => {
                                                const container =
                                                  e.currentTarget;
                                                const inner =
                                                  container.querySelector(
                                                    ".scrolling-text",
                                                  ) as HTMLElement | null;
                                                if (inner) {
                                                  const scrollDistance =
                                                    inner.scrollWidth -
                                                    container.offsetWidth;
                                                  if (scrollDistance > 0) {
                                                    const speed = 100;
                                                    const duration =
                                                      scrollDistance / speed;
                                                    inner.style.transition = `transform ${duration}s linear`;
                                                    inner.style.transform = `translateX(-${scrollDistance}px)`;
                                                  }
                                                }
                                              }}
                                              onMouseLeave={(e) => {
                                                const container =
                                                  e.currentTarget;
                                                const inner =
                                                  container.querySelector(
                                                    ".scrolling-text",
                                                  ) as HTMLElement | null;
                                                if (inner) {
                                                  inner.style.transition =
                                                    "transform 0.3s ease-out";
                                                  inner.style.transform =
                                                    "translateX(0)";
                                                }
                                              }}
                                            >
                                              <div
                                                className="scrolling-text whitespace-nowrap"
                                                title={
                                                  innerItem[
                                                    item.titleKey ?? "title"
                                                  ]
                                                }
                                              >
                                                {
                                                  innerItem[
                                                    item.titleKey ?? "title"
                                                  ]
                                                }
                                              </div>
                                            </span>
                                          </td>
                                          <td
                                            className={`flex items-center gap-[6px] whitespace-nowrap ${innerItem.status === "In Progress" && "rotating-svg"} ${innerItem.status === "Re-syncing" && "resyncing-svg"}`}
                                          >
                                            {
                                              statuses.find(
                                                (item) =>
                                                  item.text ===
                                                  innerItem.status,
                                              )?.iconComponent
                                            }
                                            {innerItem.status}
                                          </td>
                                          <td>
                                            {innerItem.size ||
                                              innerItem?.text?.length ||
                                              (innerItem?.characterCount ?? 0)}
                                          </td>
                                          <td>
                                            {isNaN(
                                              Number(innerItem.lastSyncTime),
                                            ) || !innerItem.lastSyncTime
                                              ? "..."
                                              : moment(
                                                  Number(
                                                    innerItem.lastSyncTime,
                                                  ),
                                                ).fromNow()}
                                          </td>
                                        </tr>
                                      ),
                                    )
                                  ) : (
                                    <div className="no_links_fetched pd-30 h-100 grid place-items-center">
                                      <img
                                        src="https://dlvkyia8i4zmz.cloudfront.net/7eUKmJsvR0OTNIC4DKVQ_1.svg"
                                        alt="no items available"
                                      />
                                    </div>
                                  )}
                                </tbody>
                                <tfoot className="flex h-[60px] items-center justify-between border !border-t-0 border-[rgba(0,0,0,0.10)] bg-[#f9f9f9] px-[30px] py-[16px]">
                                  <Pagination
                                    currentPage={pageValue}
                                    totalPages={Math.ceil(
                                      filteredDocs.length / itemsPerPage,
                                    )}
                                    totalItems={filteredDocs.length}
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={(newPage) =>
                                      setNativeAppPage((prev) => ({
                                        ...prev,
                                        [item.key]: newPage,
                                      }))
                                    }
                                    showFirstLast={true}
                                    showItemsInfo={true}
                                  />
                                </tfoot>
                              </table>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                </div>
              ) : null;
            })}
          </div>
        </div>
      )}
      {trainedFiles?.length || botData.aiSource?.documents?.length ? (
        <div className="uploadFile fx-d-col relative flex !h-max">
          <div className="fx-d-col flex h-max">
            <h4 className="tabs_inner_head mb-10 text-[14px] font-medium text-text-clr">
              Trained Files
            </h4>

            <div className="trained_links_wrapper files_table_wrapper new-scrollbar relative mb-0 !h-max">
              <div className="table_actions sticky left-0 top-0 z-[1] flex items-center justify-between gap-10 bg-[#f9f9f9] px-[20px] py-3">
                <div className="flex items-center gap-10">
                  <div className="search_div border-[rgba(0, 0, 0, 0.10)] flex w-[300px] items-center gap-10 rounded-[10px] border border-solid bg-white px-[16px] py-[13px]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={15}
                      height={14}
                      viewBox="0 0 15 14"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_190_16528)">
                        <path
                          d="M5.60625 0C6.98538 0 8.30802 0.547855 9.28321 1.52304C10.2584 2.49823 10.8063 3.82087 10.8063 5.2C10.8063 6.488 10.3342 7.672 9.55825 8.584L9.77425 8.8H10.4062L14.4062 12.8L13.2063 14L9.20625 10V9.368L8.99025 9.152C8.04669 9.95742 6.84682 10.3999 5.60625 10.4C4.22712 10.4 2.90448 9.85215 1.92929 8.87696C0.954105 7.90177 0.40625 6.57913 0.40625 5.2C0.40625 3.82087 0.954105 2.49823 1.92929 1.52304C2.90448 0.547855 4.22712 0 5.60625 0ZM5.60625 1.6C3.60625 1.6 2.00625 3.2 2.00625 5.2C2.00625 7.2 3.60625 8.8 5.60625 8.8C7.60625 8.8 9.20625 7.2 9.20625 5.2C9.20625 3.2 7.60625 1.6 5.60625 1.6Z"
                          fill="#444444"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_190_16528">
                          <rect
                            width={14}
                            height={14}
                            fill="white"
                            transform="translate(0.40625)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                    <input
                      type="text"
                      placeholder="Search File"
                      className="h-[unset] border-none bg-transparent p-0 outline-none"
                      value={fileSearchQuery2}
                      onChange={handleSearchFiles2}
                    />
                  </div>
                </div>
                <span
                  className={`deleteFetchedUrlBtn fx enabled al-cnt manage-btn gap-8 !bg-white`}
                  onClick={() => setSelectedSourceTab("upload_source")}
                >
                  <ManageIcon />
                  Manage Files
                </span>
              </div>
              {searchedFiles?.length ? (
                <table
                  className={`links_table w-100 mt-0 ${inProcess && "pointer-events-none opacity-50"}`}
                >
                  <thead>
                    <tr className="w-100">
                      <th className="flex items-center">
                        <span className="pl-[16px] font-medium">File Name</span>
                      </th>
                      <th>Status</th>
                      <th className="flex items-center gap-10">File Type</th>
                      <th className="flex items-center gap-10 whitespace-nowrap">
                        Size
                      </th>
                      <th className="flex items-center gap-10 whitespace-nowrap">
                        Characters
                      </th>
                      <th className="flex items-center gap-10">Last Updated</th>
                    </tr>
                  </thead>
                  <tbody className="flex !h-max flex-col items-center justify-start overflow-auto">
                    {paginatedFiles.length > 0 ? (
                      paginatedFiles?.map((file, index) => {
                        const actualIndex =
                          (fileCurrentPage - 1) * itemsPerPage + index;
                        return (
                          <tr
                            className={`w-100 flex flex-col ${file.status === "In Progress" || inProcess ? "pointer-events-none opacity-50" : ""}`}
                            key={actualIndex}
                          >
                            <td className="flex items-center">
                              <span
                                className="ml-[16px] overflow-hidden whitespace-nowrap"
                                onMouseEnter={(e) => {
                                  const container = e.currentTarget;
                                  const inner = container.querySelector(
                                    ".scrolling-text",
                                  ) as HTMLElement | null;
                                  if (inner) {
                                    const scrollDistance =
                                      inner.scrollWidth - container.offsetWidth;
                                    if (scrollDistance > 0) {
                                      const speed = 100; // pixels per second
                                      const duration = scrollDistance / speed;
                                      inner.style.transition = `transform ${duration}s linear`;
                                      inner.style.transform = `translateX(-${scrollDistance}px)`;
                                    }
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.currentTarget;
                                  const inner = container.querySelector(
                                    ".scrolling-text",
                                  ) as HTMLElement | null;
                                  if (inner) {
                                    inner.style.transition =
                                      "transform 0.3s ease-out";
                                    inner.style.transform = "translateX(0)";
                                  }
                                }}
                              >
                                <div
                                  className="scrolling-text whitespace-nowrap"
                                  title={file?.title || file?.name}
                                >
                                  {file?.title || file?.name}
                                </div>
                              </span>
                            </td>
                            <td
                              className={`flex items-center gap-[6px] whitespace-nowrap ${file.status === "In Progress" && "rotating-svg"}`}
                            >
                              {
                                statuses.find(
                                  (item) => item.text === file.status,
                                )?.iconComponent
                              }
                              {file.status}
                            </td>
                            <td className="uppercase">
                              {renderFileType(file?.title || file?.name)}
                            </td>

                            <td>
                              {file?.size / (1024 * 1024) >= 0.01
                                ? (file?.size / (1024 * 1024)).toFixed(2) +
                                  " MB"
                                : (file?.size / 1024).toFixed(2) + " KB"}
                            </td>
                            <td>{file?.characterCount}</td>
                            <td>
                              {isNaN(Number(file.lastSyncTime)) ||
                              !file.lastSyncTime
                                ? "..."
                                : moment(Number(file.lastSyncTime)).fromNow()}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <div className="no_links_fetched pd-30 h-100 grid place-items-center">
                        <img
                          src="https://dlvkyia8i4zmz.cloudfront.net/7eUKmJsvR0OTNIC4DKVQ_1.svg"
                          alt="no links fetched"
                        />
                      </div>
                    )}
                  </tbody>
                  <tfoot className="flex h-[60px] items-center justify-between bg-[#f9f9f9] px-[30px] py-[16px]">
                    <Pagination
                      currentPage={fileCurrentPage}
                      totalPages={Math.ceil(
                        searchedFiles.length / itemsPerPage,
                      )}
                      totalItems={searchedFiles.length}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handleFilePageChange}
                      showFirstLast={true}
                      showItemsInfo={true}
                    />
                  </tfoot>
                </table>
              ) : (
                <div className="no_links_fetched pd-30 h-100 grid place-items-center">
                  <img
                    src="https://dlvkyia8i4zmz.cloudfront.net/7eUKmJsvR0OTNIC4DKVQ_1.svg"
                    alt="no links fetched"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
      {trainedSource?.textArea && (
        <TextAreaComponent
          selectedSourceTab={selectedSourceTab}
          textContentId={botData.aiSource?.textArea?.contentId}
          characterUsed={characterUsed}
          characterLimit={characterLimit}
          allTrained={true}
          onDataChange={handlePageChange}
          isOld={isOld}
        />
      )}
    </div>
  ) : (
    <div className="no_trained_files grid w-full place-items-center">
      <img
        src="https://dlvkyia8i4zmz.cloudfront.net/sUPqM67mSySIT1XsHjWt_Frame_14660.svg"
        alt="no data found"
      />
    </div>
  );
};

const appCards: Array<{
  title: string;
  subtitle: string;
  icon: any;
  key: ConfigId;
  titleKey?: string;
}> = [
  {
    title: "Notion",
    subtitle: "Ingest pages from Notion",
    icon: <NotionIcon />,
    key: "notion",
  },
  {
    title: "Confluence",
    subtitle: "Ingest pages or blogposts from Confluence",
    icon: <ConfluenceIcon />,
    key: "confluence",
  },
  {
    title: "Salesforce",
    subtitle: "Ingest articles from Salesforce",
    icon: <SalesforceIcon />,
    key: "salesforce",
  },
  {
    title: "Google Drive",
    subtitle: "Ingest files from Google Drive",
    icon: <GoogleDriveIcon />,
    key: "googlepicker",
  },
];
const order = [
  {
    text: "Ascending",
    iconComponent: <AscendingOrderSvg />,
  },
  {
    text: "Descending",
    iconComponent: <DescendingOrderSVG />,
  },
];
const statuses = [
  { text: "All Statuses" },
  {
    iconComponent: <TrainingCompletedIcon />,
    text: "Training Complete",
  },
  {
    iconComponent: <TrainingProgressIcon />,
    text: "In Progress",
  },
  {
    iconComponent: <LinkRejectedIcon />,
    text: "Link Rejected",
  },
  {
    iconComponent: <LinkRejectedIcon />,
    text: "File Rejected",
  },
  {
    iconComponent: <ReSyncingIcon />,
    text: "Re-syncing",
  },
  {
    iconComponent: <ReSyncingFailedIcon />,
    text: "Re-sync Failed",
  },
];

export default AllTrainedFiles;
